import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  Briefcase,
  BookOpen,
  FolderOpen,
  MessageSquare,
  Users,
  Image,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Moon,
  Sun,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth';
import { useTheme } from '@/hooks/useTheme';
import { LanguageSwitcher } from '@/components/common';
import { useTranslation } from '@/features/i18n';

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const sidebarItems = [
    { icon: LayoutDashboard, label: t.admin.sidebar.dashboard, href: '/admin/dashboard' },
    { icon: FileText, label: t.admin.sidebar.pages, href: '/admin/pages' },
    { icon: Briefcase, label: t.admin.sidebar.services, href: '/admin/services' },
    { icon: BookOpen, label: t.admin.sidebar.blog, href: '/admin/blog' },
    { icon: FolderOpen, label: t.admin.sidebar.caseStudies, href: '/admin/case-studies' },
    { icon: MessageSquare, label: t.admin.sidebar.testimonials, href: '/admin/testimonials' },
    { icon: Users, label: t.admin.sidebar.leads, href: '/admin/leads' },
    { icon: Image, label: t.admin.sidebar.media, href: '/admin/media' },
    { icon: Settings, label: t.admin.sidebar.settings, href: '/admin/settings' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-[280px] sm:w-64 bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700 transform transition-transform duration-200 ease-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-neutral-200 dark:border-neutral-700">
            <Link to="/admin/dashboard" className="font-bold text-lg text-neutral-900 dark:text-white">
              {t.admin.layout.adminPanel}
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3 overscroll-contain">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'flex items-center px-3 py-3 sm:py-2 rounded-lg text-sm font-medium transition-colors active:scale-[0.98]',
                      isActive(item.href)
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-700'
                    )}
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 flex-shrink-0">
            <Link
              to="/admin/profile"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center px-3 py-3 sm:py-2 rounded-lg text-sm font-medium text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-700 transition-colors active:scale-[0.98]"
            >
              <User className="w-5 h-5 mr-3 flex-shrink-0" />
              {t.admin.sidebar.profile}
            </Link>
            <button
              onClick={() => {
                setSidebarOpen(false);
                handleSignOut();
              }}
              className="w-full flex items-center px-3 py-3 sm:py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors active:scale-[0.98]"
            >
              <LogOut className="w-5 h-5 mr-3 flex-shrink-0" />
              {t.admin.layout.logout}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 sm:h-16 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between h-full px-3 sm:px-4 lg:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-1 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 active:scale-95 transition-transform"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center space-x-1 sm:space-x-3">
              {/* Language switcher - hidden on very small screens */}
              <div className="hidden xs:block sm:block">
                <LanguageSwitcher />
              </div>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors active:scale-95"
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* View site link */}
              <Link
                to="/"
                target="_blank"
                className="hidden md:block px-3 py-1.5 text-sm text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
              >
                {t.admin.layout.viewSite}
              </Link>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors active:scale-95"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      {profile?.full_name?.[0] || profile?.email?.[0]?.toUpperCase() || 'A'}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-neutral-500 hidden sm:block" />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setUserMenuOpen(false)}
                      />
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute right-0 mt-2 w-56 bg-white dark:bg-neutral-800 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-700 py-2 z-50"
                      >
                        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
                          <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                            {profile?.full_name || 'Admin'}
                          </p>
                          <p className="text-xs text-neutral-500 truncate">
                            {profile?.email}
                          </p>
                        </div>
                        {/* Language switcher for mobile - shown only on small screens */}
                        <div className="px-4 py-2 border-b border-neutral-200 dark:border-neutral-700 sm:hidden">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t.admin.layout.language}</span>
                            <LanguageSwitcher />
                          </div>
                        </div>
                        {/* View site link for mobile */}
                        <Link
                          to="/"
                          target="_blank"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 md:hidden"
                        >
                          {t.admin.layout.viewSite}
                        </Link>
                        <Link
                          to="/admin/profile"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 active:bg-neutral-100"
                        >
                          <User className="w-4 h-4 mr-2" />
                          {t.admin.sidebar.profile}
                        </Link>
                        <Link
                          to="/admin/settings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 active:bg-neutral-100"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          {t.admin.sidebar.settings}
                        </Link>
                        <div className="my-1 border-t border-neutral-200 dark:border-neutral-700" />
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            handleSignOut();
                          }}
                          className="w-full flex items-center px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 active:bg-red-50"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          {t.admin.layout.logout}
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
